import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Install chart library: npm install recharts

const Home = () => {
  const navigate = useNavigate();
  const [spinner, setSpinner] = useState(true);
  const [currs, setCurrs] = useState([]);
  const [balance, setBalance] = useState(0);
  const [username, setUsername] = useState('');
  const [clientId, setClientId] = useState('');
  const [activeTab, setActiveTab] = useState('FOREX');
  const [metalsData, setMetalsData] = useState([]);
  const [cryptoData, setCryptoData] = useState([]);

  // Generate chart data for web
  const generateChartData = (basePrice, change, volatility = 5) => {
    const data = [];
    const currentPrice = parseFloat(basePrice) || 100;
    const changePercent = parseFloat(change) || 0;
    const isPositive = changePercent >= 0;
    
    for (let i = 0; i < 12; i++) {
      const randomVariation = (Math.random() - 0.5) * volatility;
      const trend = isPositive ? 0.1 : -0.1;
      const point = currentPrice * (1 + (trend * i / 12) + randomVariation / 100);
      data.push({
        time: i.toString(),
        value: Math.max(point, currentPrice * 0.9)
      });
    }
    
    return data;
  };

  // Page refresh function
  const pageRefresh = useCallback(async () => {
    try {
      const userid = localStorage.getItem('userid');
      const name = localStorage.getItem('name');
      setUsername(name || '');
      setClientId(userid || '');

      if (!userid) {
        toast.error('Please login first');
        navigate('/login');
        return;
      }

      // Fetch wallet balance
      const wurl = `https://octafxx.in/Rest/home_wallet_api/${userid}`;
      const response = await fetch(wurl);
      const responseJson = await response.json();
      console.log('Wallet response:', responseJson);
      setBalance(responseJson.bal || 0);
    } catch (error) {
      console.error('Wallet error:', error);
      toast.error('Failed to load wallet balance');
    }
  }, [navigate]);

  // Fetch market data
  const fetchMarketData = useCallback(async () => {
    try {
      const url = 'https://octafxx.in/Rest/home_forex_api/';
      const response = await fetch(url);
      const responseJson = await response.json();
      
      // Adjust this based on your actual API response structure
      setCurrs(responseJson.forex || responseJson || []);
      setMetalsData(responseJson.metals || []);
      setCryptoData(responseJson.crypto || []);
      setSpinner(false);
    } catch (error) {
      console.error('Market data error:', error);
      toast.error('Failed to load market data');
      setSpinner(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const isLoggedIn = localStorage.getItem('LoggedIn');
    if (!isLoggedIn || isLoggedIn !== '1') {
      navigate('/login');
      return;
    }

    pageRefresh();
    fetchMarketData();

    // Set up interval for live updates
    const interval = setInterval(() => {
      fetchMarketData();
    }, 20000); // Update every 20 seconds for web

    return () => clearInterval(interval);
  }, [pageRefresh, fetchMarketData, navigate]);

  // Get data for active tab
  const getTabData = () => {
    switch (activeTab) {
      case 'FOREX':
        return currs;
      case 'METALS':
        return metalsData;
      case 'CRYPTO':
        return cryptoData;
      default:
        return currs;
    }
  };

  // Enhanced quote card component
  const EnhancedQuoteCard = ({ item, index }) => {
    const isPositive = Math.random() > 0.5;
    const chartData = generateChartData(item.cbuy_price, isPositive ? 1.25 : -0.85);
    const changePercent = ((Math.random() * 2.5) + 0.5).toFixed(2);
    
    return (
      <div className="col-lg-4 col-md-6 mb-4">
        <div className="card h-100 shadow-sm border-0 hover-shadow transition-all" 
             style={{ cursor: 'pointer', transition: '0.3s' }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${isPositive ? 'bg-success' : 'bg-danger'}`} 
                     style={{ width: '40px', height: '40px' }}>
                  <i className={`bi ${activeTab === 'CRYPTO' ? 'bi-currency-bitcoin' : 
                                  activeTab === 'METALS' ? 'bi-gem' : 'bi-graph-up'} text-white`}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold text-dark">{item.curr_name}</h6>
                  <small className="text-muted">#{item.curr_code || index}</small>
                </div>
              </div>
              <div className={`badge ${isPositive ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'} p-2`}>
                <i className={`bi ${isPositive ? 'bi-arrow-up' : 'bi-arrow-down'} me-1`}></i>
                {isPositive ? '+' : ''}{changePercent}%
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <small className="text-muted d-block">SELL</small>
                <h5 className={`mb-0 ${item.sell_color === 'green' ? 'text-success' : 'text-danger'}`}>
                  {item.csell_price}
                </h5>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">BUY</small>
                <h5 className={`mb-0 ${item.buy_color === 'green' ? 'text-success' : 'text-danger'}`}>
                  {item.cbuy_price}
                </h5>
              </div>
            </div>

            {/* Chart */}
            <div style={{ height: '80px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={isPositive ? "#10b981" : "#ef4444"} 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="badge bg-light text-dark p-2">
                <i className="bi bi-volume-up me-1"></i>
                Vol: {(Math.random() * 1000).toFixed(0)}K
              </div>
              <button className="btn btn-sm btn-outline-primary">
                Trade <i className="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Tab bar component
  const TabBar = () => {
    const tabs = ['FOREX', 'METALS', 'CRYPTO'];
    
    return (
      <div className="d-flex mb-4">
        <div className="btn-group" role="group">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`btn ${activeTab === tab ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
    );
  };


  const tabData = getTabData();

  return (
    <div className="container-fluid bg-light min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Loading Overlay */}
      {spinner && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50" style={{ zIndex: 9999 }}>
          <div className="text-center text-white">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading Market Data...</p>
          </div>
        </div>
      )}

      <div className="container py-4">
        {/* Welcome Card */}
        <div className="card bg-primary text-white shadow-lg mb-4 border-0">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle bg-white bg-opacity-25 p-3 me-3">
                    <i className="bi bi-person-circle fs-2"></i>
                  </div>
                  <div>
                    <small className="opacity-75">Welcome back</small>
                    <h4 className="mb-0 fw-bold">{username || 'Trader'}</h4>
                    <small>Account: #{clientId ? clientId.padStart(5, '0') : '00000'}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-md-end">
                  <small className="opacity-75">Available Balance</small>
                  <h2 className="mb-0 fw-bold">${parseFloat(balance).toFixed(2)}</h2>
                  <div className="mt-3">
                    <button className="btn btn-light me-2" onClick={() => navigate('/deposit')}>
                      <i className="bi bi-plus-circle me-1"></i> Deposit
                    </button>
                    <button className="btn btn-outline-light" onClick={() => navigate('/withdraw')}>
                      <i className="bi bi-arrow-up-circle me-1"></i> Withdraw
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="card-title mb-0 fw-bold">
                <i className="bi bi-graph-up me-2"></i>
                Market Watch
              </h5>
              <div className="d-flex align-items-center">
                <span className="text-muted me-3">Live Updates</span>
                <div className="spinner-grow spinner-grow-sm text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>

            <TabBar />

            {tabData.length > 0 ? (
              <div className="row">
                {tabData.slice(0, 6).map((item, index) => (
                  <EnhancedQuoteCard key={index} item={item} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-graph-up display-1 text-muted mb-3"></i>
                <h5 className="text-muted">No {activeTab} data available</h5>
                <p className="text-muted">Market data will appear here once available</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="row">
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-white border-0">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-clock-history me-2"></i>
                  Recent Trades
                </h6>
              </div>
              <div className="card-body">
                <div className="list-group list-group-flush">
                  {[1, 2, 3].map((trade, index) => (
                    <div key={index} className="list-group-item border-0 px-0 py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="badge bg-light text-dark mb-1">EUR/USD</div>
                          <div className="small text-muted">Buy • Market Order</div>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold text-success">+$24.50</div>
                          <div className="small text-muted">2 mins ago</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-outline-primary w-100 mt-3" onClick={() => navigate('/trades')}>
                  View All Trades <i className="bi bi-arrow-right ms-1"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-white border-0">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-bell me-2"></i>
                  Notifications
                </h6>
              </div>
              <div className="card-body">
                <div className="list-group list-group-flush">
                  {[1, 2, 3].map((note, index) => (
                    <div key={index} className="list-group-item border-0 px-0 py-3">
                      <div className="d-flex">
                        <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${index === 0 ? 'bg-warning' : 'bg-light'}`}
                             style={{ width: '40px', height: '40px' }}>
                          <i className={`bi ${index === 0 ? 'bi-exclamation-triangle' : 'bi-info-circle'} ${index === 0 ? 'text-white' : 'text-primary'}`}></i>
                        </div>
                        <div>
                          <div className="fw-bold">Market Update</div>
                          <div className="small text-muted">EUR/USD showing strong bullish trend</div>
                          <div className="small text-muted">10 mins ago</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-outline-primary w-100 mt-3" onClick={() => navigate('/notifications')}>
                  View All Notifications <i className="bi bi-arrow-right ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-dark text-white py-4 mt-auto">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <h6 className="fw-bold mb-3">TradeFX Platform</h6>
              <p className="small opacity-75">Professional trading platform with real-time market data and analytics.</p>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="mb-3">
                <small className="opacity-75">© {new Date().getFullYear()} TradeFX. All rights reserved.</small>
              </div>
              <div>
                <a href="/terms" className="text-white opacity-75 me-3 text-decoration-none">Terms</a>
                <a href="/privacy" className="text-white opacity-75 me-3 text-decoration-none">Privacy</a>
                <a href="/contact" className="text-white opacity-75 text-decoration-none">Contact</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;